import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICategory } from "../../interfaces/ICategory";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IJudgmentType } from "../../interfaces/IJudgmentType";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import { ISpecie } from "../../interfaces/ISpecie";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const Category: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();

  const [categoryId, setCategoryId] = useState("");
  const [judgmentTypesOpions, setJudgmentTypesOptions] = useState<IOption[]>(
    [],
  );
  const [judgmentTypeOption, setJudgmentTypeOption] = useState<IOption | null>(
    null,
  );

  const [speciesOptions, setSpeciesOptions] = useState<IOption[]>([]);
  const [specieOption, setSpecieOption] = useState<IOption | null>(null);

  const [sequence, setSequence] = useState("");
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const handleChangeIsActive = (event: ChangeEvent<HTMLInputElement>) => {
    setIsActive(event.target.checked);
  };

  const judgmentTypeToOption = (judgmentTypes: IJudgmentType): IOption => {
    return {
      value: `${judgmentTypes.id}`,
      description: `${judgmentTypes.id} - ${judgmentTypes.name}`,
    };
  };

  const specieToOption = (species: ISpecie): IOption => {
    return {
      value: `${species.id}`,
      description: `${species.name}`,
    };
  };

  const handleSetJudgmentType = async (value: string | null) => {
    if (value) {
      const selected = judgmentTypesOpions.find(judgmentTypes => {
        return judgmentTypes.value === value;
      });

      if (selected?.value) {
        setJudgmentTypeOption(selected);
        await handleRequestNextSequence(Number(selected.value));
      } else {
        setJudgmentTypeOption(null);
        setSequence("");
      }
    }
  };

  const handleSetSpecie = async (value: string | null) => {
    if (value) {
      const selected = speciesOptions.find(specie => {
        return specie.value === value;
      });

      if (selected?.value) {
        setSpecieOption(selected);
      } else {
        setSpecieOption(null);
      }
    }
  };

  const handleRequestNextSequence = useCallback(
    async (judgmentTypeId: number) => {
      setLoading(true);
      await api
        .get(`/categories/next-sequence?judgmentTypeId=${judgmentTypeId}`)
        .then(response => {
          const nextSequence: number = response.data;
          setSequence(nextSequence.toString());
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          } else if (error.status === 404) {
            setSequence("");
          } else {
            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          }
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [navigate, signOut, toastr],
  );

  const handleResquestJudgmentTypes = useCallback(async () => {
    setLoading(true);

    await api
      .get(
        `/judgment-types?currentPage=1&perPage=100&orderField=id&orderBy=id&orderDirection=asc`,
      )
      .then(async response => {
        const judgmentTypes: IJudgmentType[] = response.data.data;

        const judgmentTypesOptions = judgmentTypes.map(judgmentTypeToOption);
        setJudgmentTypesOptions(judgmentTypesOptions);

        const selected = judgmentTypesOptions.find(judgmentTypes => {
          return judgmentTypes.value === judgmentTypeOption?.value;
        });

        if (selected?.value) {
          setJudgmentTypeOption(selected);
          await handleRequestNextSequence(Number(selected.value));
        } else {
          setJudgmentTypeOption(judgmentTypesOptions[0]);
          await handleRequestNextSequence(
            Number(judgmentTypesOptions[0].value),
          );
        }
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }

        toastr.error(
          error.response.data?.message || "Contate a equipe de suporte",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    judgmentTypeOption?.value,
    navigate,
    signOut,
    toastr,
    handleRequestNextSequence,
  ]);

  const handleResquestSpecies = useCallback(async () => {
    setLoading(true);

    await api
      .get(
        `/species?currentPage=1&perPage=100&orderField=id&orderBy=id&orderDirection=asc`,
      )
      .then(async response => {
        const species: ISpecie[] = response.data.data;

        const speciesOptions = species.map(specieToOption);
        setSpeciesOptions(speciesOptions);
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }

        toastr.error(
          error.response.data?.message || "Contate a equipe de suporte",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, signOut, toastr]);

  const handleListCategory = useCallback(async () => {
    const id = location.pathname.replace("/category", "").replace("/", "");

    await Promise.all([handleResquestJudgmentTypes(), handleResquestSpecies()]);

    if (id) {
      setCategoryId(id);
      setLoading(true);

      await api
        .get(`/categories/${id}`)
        .then(response => {
          const category: ICategory = response.data;

          setName(category.name ? category.name : "");
          setDetails(category.details ? category.details : "");
          setIsActive(!!category.isActive);

          category.judgmentType &&
            setJudgmentTypeOption(judgmentTypeToOption(category.judgmentType));

          category.specie && setSpecieOption(specieToOption(category.specie));

          setSequence(category.sequence ? category.sequence.toString() : "");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    navigate,
    signOut,
    toastr,
    location.pathname,
    handleResquestJudgmentTypes,
    handleResquestSpecies,
  ]);

  const handleDeleteCategory = useCallback(async () => {
    const id = location.pathname.replace("/category", "").replace("/", "");

    if (id) {
      setLoading(true);

      await api
        .delete(`/categories/${id}`)
        .then(() => {
          navigate("/categories");
          toastr.success("Categoria excluída com sucesso");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});
      setLoading(true);

      const schema = Yup.object().shape({
        judgmentTypeId: Yup.number()
          .moreThan(0, "Selecione um tipo de julgamento")
          .required("Tipo de julgamento é obrigatório"),
        name: Yup.string().required("Nome é obrigatório"),
        sequence: Yup.number()
          .typeError("Sequência deve ser um número")
          .moreThan(0, "Sequência deve ser maior que 0")
          .lessThan(100, "Sequência deve ser menor que 100")
          .required("Sequência é obrigatório"),
      });

      if (categoryId) {
        const data: ICategory = {
          judgmentTypeId: Number(judgmentTypeOption?.value),
          sequence: sequence ? Number(sequence) : 0,
          name,
          details,
          isActive,
          specieId: specieOption?.value,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/categories/${categoryId}`, data)
          .then(async () => {
            await handleListCategory();
            toastr.success("Categoria atualizada com sucesso");
            navigate("/categories");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: ICategory = {
          judgmentTypeId: Number(judgmentTypeOption?.value),
          sequence: sequence ? Number(sequence) : 0,
          name,
          details,
          specieId: specieOption?.value,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/categories`, data)
          .then(() => {
            toastr.success("Categoria cadastrada com sucesso");
            navigate("/categories");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    categoryId,
    sequence,
    name,
    details,
    isActive,
    judgmentTypeOption,
    specieOption,
    navigate,
    signOut,
    toastr,
    handleListCategory,
  ]);

  useEffect(() => {
    handleListCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Categoria</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={!!formErrors.judgmentTypeId}
              >
                <InputLabel
                  id="select-filter"
                  error={!!formErrors.judgmentTypeId}
                  required
                >
                  Tipo de Julgamento
                </InputLabel>
                <Select
                  label="Tipo de Julgamento"
                  value={judgmentTypeOption?.value || ""}
                  onChange={e => handleSetJudgmentType(String(e.target.value))}
                  error={!!formErrors.judgmentTypeId}
                  required
                  autoFocus
                >
                  {judgmentTypesOpions.map(judgmentType => {
                    return (
                      <MenuItem
                        key={judgmentType.description}
                        value={judgmentType.value}
                      >
                        {judgmentType.description}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{formErrors.judgmentTypeId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"999"}
                value={sequence || ""}
                onChange={e => setSequence(e.target.value)}
                maskPlaceholder={null}
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Sequência"
                  required
                  helperText={formErrors.sequence}
                  error={!!formErrors.sequence}
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.specieId}>
                <InputLabel
                  id="select-filter"
                  error={!!formErrors.specieId}
                  required
                >
                  Espécie
                </InputLabel>
                <Select
                  label="Espécie"
                  value={specieOption?.value || ""}
                  onChange={e => handleSetSpecie(String(e.target.value))}
                  error={!!formErrors.specieId}
                  required
                  autoFocus
                >
                  {speciesOptions.map(specie => {
                    return (
                      <MenuItem key={specie.description} value={specie.value}>
                        {specie.description}
                      </MenuItem>
                    );
                  })}
                </Select>
                <FormHelperText>{formErrors.specieId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Observação"
                value={details}
                onChange={e => setDetails(e.target.value)}
              />
            </Grid>

            {categoryId && (
              <Grid item xs={12} md={6} sm={6}>
                <FormControlLabel
                  label="Ativo"
                  control={
                    <Checkbox
                      checked={isActive}
                      onChange={handleChangeIsActive}
                    />
                  }
                />
              </Grid>
            )}
          </Grid>
          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={() => navigate("/categories")}
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {categoryId && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Excluir
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={categoryId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${categoryId}`}
          handleConfirmation={handleDeleteCategory}
          deny={() => setDialogDeleteOpen(false)}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
